/**
 * 总览。三种角色看到的是同一段代码 —— 数据范围由后端按 `path LIKE` 收敛，
 * 前端只决定「多显示哪几块」：
 *
 * - 平台：额外显示试算平衡与余额漂移（全局口径，代理/商户看不到也不该看到）
 * - 平台 / 代理：额外显示分通道成功率、分润、商户排行
 * - 商户：这些块换成自己的资金概览
 */

import { useMemo, useState } from 'react';
import { useAsync } from '../lib/hooks.ts';
import { useSession } from '../lib/session.tsx';
import { ScopeBanner } from '../components/scope-banner.tsx';
import { Card, ErrorBox, Loading, Metric, Money, Badge, Empty, CopyId } from '../components/ui.tsx';
import { DataTable, type Column } from '../components/table.tsx';
import { formatRatio, formatMinor, toBig } from '../lib/money.ts';
import { ORG_TYPE_LABEL, formatDate } from '../lib/format.ts';
import type {
  ChannelStats, Drift, Funds, MerchantRanking, Overview, RevenueReport, Timeseries, TrialBalance,
} from '../lib/types.ts';

const WINDOWS = [
  { value: '7', label: '近 7 天' },
  { value: '30', label: '近 30 天' },
  { value: '90', label: '近 90 天' },
];

export function OverviewPage() {
  const session = useSession();
  const [days, setDays] = useState('30');

  /*
   * `from` **必须**随 days 记忆化。
   *
   * 它是下面每个 useAsync 的依赖，而 useAsync 的依赖一变就重新取数、取完 setState、
   * setState 又触发一次渲染。如果这里每次渲染都用 Date.now() 现算一个新值，
   * 这条链就闭合成死循环：取数 → 渲染 → 新 from → 再取数，一秒能打出几十个请求，
   * 而且页面看起来完全正常，只是一直在转。
   *
   * 这不是假设 —— 上线后 nginx 日志里 /analytics/overview 累计了两百多万次请求，
   * 峰值每秒 37 个，全是这一行造成的。
   *
   * 记忆化之后，只有用户真的切换时间窗口才会重新取数。
   */
  const from = useMemo(
    () => new Date(Date.now() - Number(days) * 86_400_000).toISOString(),
    [days],
  );

  const overview = useAsync<Overview>('/v1/analytics/overview', { query: { from } }, [from]);
  const series = useAsync<Timeseries>(
    '/v1/analytics/timeseries',
    { query: { from, interval: Number(days) > 30 ? 'week' : 'day' } },
    [from, days],
  );

  return (
    <>
      <div className="row">
        <h1>经营总览</h1>
        <ScopeBanner context="这些通道的数据。下面的金额只统计成交在这些通道上的交易，笔数则包含你的通道尝试过的全部交易。" />
        <span style={{ flex: 1 }} />
        <div className="row tight">
          {WINDOWS.map((window) => (
            <button
              key={window.value}
              className="btn"
              data-size="sm"
              {...(days === window.value ? { 'data-variant': 'primary' as const } : {})}
              onClick={() => { setDays(window.value); }}
            >
              {window.label}
            </button>
          ))}
        </div>
      </div>

      <ErrorBox error={overview.error} />

      {overview.loading && overview.data === null ? (
        <Loading />
      ) : overview.data !== null ? (
        <VolumeCards data={overview.data} />
      ) : null}

      {series.data !== null && series.data.data.length > 0 && (
        <Card title="成交走势" sub={`按${series.data.interval === 'week' ? '周' : '日'}汇总请款金额`}>
          <Sparkline data={series.data} />
        </Card>
      )}

      {session.isMerchant && <MerchantFunds />}
      {/*
        * 分通道成功率对被收窄的成员**恰恰是最有用的一块**：它按 attempt 聚合，
        * 通道维度是单值的，他拿到的每个数都精确属于自己负责的通道。所以照常显示。
        */}
      {!session.isMerchant && <ChannelHealth from={from} />}
      {/*
        * 分润与账本健康两块对受限成员不显示。
        *
        * 两者都是全平台口径，混着别的分组的钱，服务端对受限成员 403（assertNotChannelScoped）。
        * 不摘掉的话页面上会挂红色报错 —— 而且这个页面的取数是随时间窗口重取的，
        * 报错块会跟着一遍遍重试。
        */}
      {!session.isMerchant && !session.isChannelScoped && !session.isMoneyHidden && <RevenueBlock from={from} />}
      {/*
        * 商户排行榜对分组成员放开：/analytics/merchants 服务端本来就按通道收窄
        * （paymentChannelScopeFilter + scopedMoneySum），他只会看到本组商户的本组收入。
        * 营收（分润）与账本健康度是全平台口径，混着别的分组的钱，继续不画。
        */}
      {!session.isMerchant && !session.isMoneyHidden && <MerchantLeaderboard from={from} />}
      {session.isPlatform && !session.isChannelScoped && !session.isMoneyHidden && <LedgerHealth />}
    </>
  );
}

function VolumeCards({ data }: { data: Overview }) {
  const attempts = data.attempts;
  const successRate = formatRatio(attempts.success_rate);
  const failedCount = toBig(attempts.failed) ?? 0n;

  return (
    <>
      <div className="grid cols-4">
        <Card>
          <Metric
            label="尝试总数"
            value={attempts.total}
            note={`成功 ${attempts.succeeded} · 失败 ${attempts.failed}`}
          />
        </Card>
        <Card>
          <Metric
            label="成功率"
            value={successRate}
            tone={
              attempts.success_rate === null ? undefined
                : Number(attempts.success_rate) >= 0.9 ? 'good'
                : Number(attempts.success_rate) >= 0.7 ? 'warn' : 'bad'
            }
            note={
              attempts.success_rate === null
                ? '窗口内没有任何尝试'
                : '按尝试数计算，一笔支付重路由多次会分别计入'
            }
          />
        </Card>
        <Card>
          <Metric
            label="失败尝试"
            value={attempts.failed}
            tone={failedCount > 0n ? 'warn' : undefined}
            note="含硬拒付、软拒付与网络错误"
          />
        </Card>
        <Card>
          <Metric
            label="争议"
            value={data.disputes.reduce((sum, row) => sum + Number(row.count), 0)}
            tone={data.disputes.length > 0 ? 'bad' : undefined}
            note={data.disputes.length === 0 ? '窗口内没有争议' : '拒付与调单合计'}
          />
        </Card>
      </div>

      {data.volume.length === 0 ? (
        <Card title="交易额">
          <Empty title="窗口内没有交易" hint="这里统计的是 payments.captured_amount —— 下单额不等于收到的钱，只有请款成功的部分才算成交。" />
        </Card>
      ) : (
        <div className="grid cols-3">
          {data.volume.map((row) => (
            <Card key={row.currency} title={`交易额 · ${row.currency}`} sub={`${row.payment_count} 笔`}>
              <div className="stack">
                <Metric
                  label="已请款"
                  value={formatMinor(row.captured_amount, row.currency)}
                  currency={row.currency}
                />
                <div className="grid cols-2">
                  <Metric
                    label="已退款"
                    value={formatMinor(row.refunded_amount, row.currency)}
                    tone={(toBig(row.refunded_amount) ?? 0n) > 0n ? 'warn' : undefined}
                  />
                  <Metric label="净额" value={formatMinor(row.net_amount, row.currency)} />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * 走势条。不引图表库 —— 一个按币种分组的柱状走势用 flex 就够了，
 * 引一个几百 KB 的依赖来画这个不划算。
 *
 * 高度按窗口内最大值归一。全程 bigint 比较，只在算百分比时才转数字
 *（那时已经是 0..100 的比例，不是金额）。
 */
function Sparkline({ data }: { data: Timeseries }) {
  const currencies = [...new Set(data.data.map((row) => row.currency))];

  return (
    <div className="stack">
      {currencies.map((currency) => {
        const points = data.data.filter((row) => row.currency === currency);
        const max = points.reduce((best, row) => {
          const value = toBig(row.captured_amount) ?? 0n;
          return value > best ? value : best;
        }, 0n);

        return (
          <div key={currency} className="stack tight">
            <div className="row">
              <span className="metric-label">{currency}</span>
              <span style={{ flex: 1 }} />
              <span className="card-sub">
                峰值 {formatMinor(max, currency)} {currency}
              </span>
            </div>
            <div className="spark">
              {points.map((row) => {
                const value = toBig(row.captured_amount) ?? 0n;
                // 比例算完才转 number：分子分母都是 bigint，先放大再整除
                const percent = max === 0n ? 0 : Number((value * 100n) / max);
                return (
                  <div
                    key={row.bucket}
                    className="spark-bar"
                    style={{ height: `${Math.max(percent, value > 0n ? 3 : 1)}%` }}
                    title={`${formatDate(row.bucket)} · ${formatMinor(value, currency)} ${currency} · ${row.payment_count} 笔`}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** 商户视角的资金概览。 */
function MerchantFunds() {
  const session = useSession();
  const [currency, setCurrency] = useState('USD');
  const funds = useAsync<Funds>(
    '/v1/ledger/funds',
    { query: { org_id: session.me.org.id, currency } },
    [currency, session.me.org.id],
  );

  return (
    <Card
      title="我的资金"
      sub="待结算会在通道结算周期后转为可用"
      actions={
        <input
          className="input"
          style={{ width: 96 }}
          value={currency}
          onChange={(event) => { setCurrency(event.target.value.toUpperCase()); }}
        />
      }
    >
      {funds.error !== null ? (
        <ErrorBox error={funds.error} />
      ) : funds.data === null ? (
        <Loading />
      ) : (
        <div className="grid cols-4">
          <Metric label="待结算" value={formatMinor(funds.data.pending, currency)} currency={currency} />
          <Metric label="可用" value={formatMinor(funds.data.available, currency)} currency={currency} />
          <Metric label="风险保证金" value={formatMinor(funds.data.reserve, currency)} currency={currency} />
          <Metric
            label="可提现"
            value={formatMinor(funds.data.withdrawable.withdrawable, currency)}
            currency={currency}
            tone={funds.data.withdrawable.over_committed ? 'bad' : 'good'}
            note={
              funds.data.withdrawable.over_committed
                ? '在途提现已超过可用余额'
                : `已扣除在途提现 ${formatMinor(funds.data.withdrawable.pending_payouts, currency)}`
            }
          />
        </div>
      )}
    </Card>
  );
}

function ChannelHealth({ from }: { from: string }) {
  const stats = useAsync<ChannelStats>('/v1/analytics/channels', { query: { from } }, [from]);

  const columns: readonly Column<ChannelStats['data'][number]>[] = [
    { key: 'channel', header: '通道', render: (row) => (
      <span className="cell-strong">{row.channel_code}</span>
    ) },
    { key: 'label', header: '账户', render: (row) => <span className="cell-muted">{row.channel_label}</span> },
    { key: 'currency', header: '币种', render: (row) => row.currency },
    { key: 'attempts', header: '尝试', numeric: true, render: (row) => row.attempts },
    { key: 'rate', header: '成功率', numeric: true, render: (row) => (
      <span className={
        row.success_rate === null ? undefined
          : Number(row.success_rate) >= 0.9 ? 'cr'
          : Number(row.success_rate) < 0.7 ? 'dr' : undefined
      }>
        {formatRatio(row.success_rate)}
      </span>
    ) },
    { key: 'hard', header: '硬拒', numeric: true, render: (row) => row.hard_declines },
    { key: 'soft', header: '软拒', numeric: true, render: (row) => row.soft_declines },
    { key: 'network', header: '网络错', numeric: true, render: (row) => (
      <span className={Number(row.network_errors) > 0 ? 'dr' : undefined}>{row.network_errors}</span>
    ) },
    { key: 'amount', header: '成功金额', numeric: true, render: (row) => (
      <Money value={row.succeeded_amount} currency={row.currency} />
    ) },
  ];

  return (
    <Card title="分通道表现" sub="硬拒是持卡人/发卡行的决定，软拒和网络错才是可以靠切换通道挽回的" flush>
      <DataTable
        columns={columns}
        rows={stats.data?.data ?? []}
        loading={stats.loading}
        error={stats.error}
        rowKey={(row) => `${row.channel_account_id}:${row.currency}`}
        empty="窗口内没有通道尝试"
      />
    </Card>
  );
}

function RevenueBlock({ from }: { from: string }) {
  const revenue = useAsync<RevenueReport>('/v1/analytics/revenue', { query: { from } }, [from]);

  const columns: readonly Column<RevenueReport['data'][number]>[] = [
    { key: 'org', header: '组织', render: (row) => (
      <div className="row tight">
        <span className="cell-strong">{row.org_name}</span>
        <Badge tone="neutral">{ORG_TYPE_LABEL[row.org_type]}</Badge>
      </div>
    ) },
    { key: 'currency', header: '币种', render: (row) => row.currency },
    { key: 'entries', header: '分录数', numeric: true, render: (row) => row.entries },
    { key: 'revenue', header: '分润', numeric: true, render: (row) => (
      <Money value={row.revenue} currency={row.currency} tone={(toBig(row.revenue) ?? 0n) >= 0n ? 'good' : 'bad'} />
    ) },
  ];

  return (
    <Card
      title="分润"
      sub="取自账本分录的贷方净额，不是拿费率重算的 —— 账本才是唯一真相源"
      flush
    >
      <DataTable
        columns={columns}
        rows={revenue.data?.data ?? []}
        loading={revenue.loading}
        error={revenue.error}
        rowKey={(row) => `${row.org_id}:${row.currency}`}
        empty="窗口内没有分润入账"
        emptyHint="平台的分润记在 fee_income，代理的记在 pending（要过账期）。商户不参与分润 —— 它们是手续费的支付方。"
      />
    </Card>
  );
}

function MerchantLeaderboard({ from }: { from: string }) {
  const ranking = useAsync<MerchantRanking>('/v1/analytics/merchants', { query: { from, limit: 10 } }, [from]);

  const columns: readonly Column<MerchantRanking['data'][number]>[] = [
    { key: 'merchant', header: '商户', render: (row) => (
      <div className="stack tight">
        <span className="cell-strong">{row.merchant_name}</span>
        <CopyId id={row.merchant_org_id} />
      </div>
    ) },
    { key: 'currency', header: '币种', render: (row) => row.currency },
    { key: 'count', header: '笔数', numeric: true, render: (row) => row.payment_count },
    { key: 'captured', header: '请款', numeric: true, render: (row) => (
      <Money value={row.captured_amount} currency={row.currency} />
    ) },
    { key: 'refunded', header: '退款', numeric: true, render: (row) => (
      <Money value={row.refunded_amount} currency={row.currency} tone={(toBig(row.refunded_amount) ?? 0n) > 0n ? 'bad' : 'muted'} />
    ) },
  ];

  return (
    <Card title="商户排行" sub="按请款金额，最多 10 名" flush>
      <DataTable
        columns={columns}
        rows={ranking.data?.data ?? []}
        loading={ranking.loading}
        error={ranking.error}
        rowKey={(row) => `${row.merchant_org_id}:${row.currency}`}
        empty="窗口内没有商户产生交易"
      />
    </Card>
  );
}

/**
 * 账本健康度（平台专属）。
 *
 * 这两个数是整个系统最重要的告警信号：
 * - 试算平衡不平 = 复式记账被破坏，任何一个金额都不可信了
 * - 余额漂移非空 = 物化余额与分录聚合对不上，商户看到的余额是错的
 *
 * 它们正常时应当**平淡无奇**，所以这里刻意不做成大卡片；一旦异常才变红。
 */
function LedgerHealth() {
  const trial = useAsync<TrialBalance>('/v1/ledger/trial-balance');
  const drift = useAsync<Drift>('/v1/ledger/drift');

  return (
    <div className="grid cols-2">
      <Card title="试算平衡" sub="Σ借方 必须恒等于 Σ贷方">
        {trial.error !== null ? <ErrorBox error={trial.error} />
          : trial.data === null ? <Loading />
          : trial.data.data.length === 0 ? <Empty title="账本还没有任何分录" />
          : (
            <div className="stack">
              {!trial.data.balanced && (
                <div className="alert" data-tone="bad">
                  <span className="alert-title">账本不平 —— 立即停止一切资金操作并排查</span>
                </div>
              )}
              <div className="table-scroll">
                <table className="data">
                  <thead>
                    <tr>
                      <th>币种</th>
                      <th className="num">借方</th>
                      <th className="num">贷方</th>
                      <th className="num">差额</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trial.data.data.map((row) => (
                      <tr key={row.currency}>
                        <td>{row.currency}</td>
                        <td className="num dr">{formatMinor(row.debit, row.currency)}</td>
                        <td className="num cr">{formatMinor(row.credit, row.currency)}</td>
                        <td className="num">
                          <span className={(toBig(row.difference) ?? 0n) === 0n ? 'cell-muted' : 'dr'}>
                            {formatMinor(row.difference, row.currency)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
      </Card>

      <Card title="余额漂移" sub="物化余额 vs 分录聚合">
        {drift.error !== null ? <ErrorBox error={drift.error} />
          : drift.data === null ? <Loading />
          : !drift.data.has_drift ? (
            <div className="row">
              <Badge tone="good">无漂移</Badge>
              <span className="card-sub">所有账户的物化余额与分录聚合一致</span>
            </div>
          ) : (
            <div className="stack">
              <div className="alert" data-tone="bad">
                <span className="alert-title">检测到 {drift.data.data.length} 个账户余额失真</span>
                <span>商户当前看到的余额是错的，出款前必须先修复。</span>
              </div>
              <div className="table-scroll">
                <table className="data">
                  <thead>
                    <tr>
                      <th>账户</th>
                      <th>类型</th>
                      <th className="num">物化</th>
                      <th className="num">实算</th>
                      <th className="num">漂移</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drift.data.data.map((row) => (
                      <tr key={row.account_id}>
                        <td><CopyId id={row.account_id} /></td>
                        <td>{row.kind}</td>
                        <td className="num">{formatMinor(row.materialized_balance, row.currency)}</td>
                        <td className="num">{formatMinor(row.computed_balance, row.currency)}</td>
                        <td className="num dr">{formatMinor(row.drift, row.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
      </Card>
    </div>
  );
}
